
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { motion, Variants } from "framer-motion";
import { Globe, ListFilter, ArrowDown } from "lucide-react";

interface MiniLoadingProps {
  onComplete?: () => void;
  onLinesComplete?: () => void;
  className?: string;
}

export function Loading({ className, onLinesComplete }: MiniLoadingProps) {
  // Animation State
  const [cycle, setCycle] = useState(0);
  const [buildPhase, setBuildPhase] = useState<"idle" | "building">("idle");
  const [initialLineFill, setInitialLineFill] = useState(false);
  const [step1State, setStep1State] = useState<"inactive" | "spinning" | "complete">("inactive");
  const [step2State, setStep2State] = useState<"inactive" | "spinning" | "complete">("inactive");
  const [arrowState, setArrowState] = useState<"inactive" | "active" | "complete">("inactive");
  const [linesActive, setLinesActive] = useState(false);
  const [animationComplete, setAnimationComplete] = useState(false);

  useEffect(() => {
    // Reset states at start of cycle
    // If it's the first cycle (0), we start from idle to build up.
    // If it's a subsequent cycle (>0), we keep the structure built ("building") and just reset the progress.
    
    if (cycle === 0) {
      setBuildPhase("idle");
    } else {
      setBuildPhase("building");
    }

    setInitialLineFill(false);
    setStep1State("inactive");
    setStep2State("inactive");
    setArrowState("inactive");
    setLinesActive(false);
    setAnimationComplete(false);

    // Sequence Timings (Fast & Looping)
    // Total cycle: ~2.5s (faster)

    // 1. Start Build (only needed for cycle 0)
    const startBuildTimer = setTimeout(() => {
      if (cycle === 0) setBuildPhase("building");
    }, 40);

    // 2. Build Complete & Start Progress Line (0.3s)
    const startProgressTimer = setTimeout(() => {
      // No need to set buildPhase to complete, just keep it building
      setInitialLineFill(true);
    }, 300);

    // 3. Step 1 Spin (0.5s)
    const step1Timer = setTimeout(() => {
      setStep1State("spinning");
    }, 500);

    // 4. Step 1 Complete & Line 1 Start (0.9s)
    const line1Timer = setTimeout(() => {
      setStep1State("complete");
    }, 900);

    // 5. Step 2 Spin (1.1s)
    const step2Timer = setTimeout(() => {
      setStep2State("spinning");
    }, 1100);

    // 6. Step 2 Complete & Line 2 Start (1.5s)
    const line2Timer = setTimeout(() => {
      setStep2State("complete");
    }, 1500);

    // 7. Arrow Active (1.7s)
    const arrowTimer = setTimeout(() => {
      setArrowState("active");
    }, 1700);

    // 8. Branching Lines (1.9s)
    const linesTimer = setTimeout(() => {
      setArrowState("complete");
      setLinesActive(true);
    }, 1900);

    // 9. Complete & Loop (2.5s)
    const completeTimer = setTimeout(() => {
      setAnimationComplete(true);
      onLinesComplete?.();
      // Trigger next cycle
      setTimeout(() => setCycle(c => c + 1), 200);
    }, 2500);

    return () => {
      clearTimeout(startBuildTimer);
      clearTimeout(startProgressTimer);
      clearTimeout(step1Timer);
      clearTimeout(line1Timer);
      clearTimeout(step2Timer);
      clearTimeout(line2Timer);
      clearTimeout(arrowTimer);
      clearTimeout(linesTimer);
      clearTimeout(completeTimer);
    };
  }, [cycle, onLinesComplete]);

  const getStepStyles = (state: "inactive" | "spinning" | "complete") => {
    switch (state) {
      case "inactive":
        return {
          container: "border-slate-200",
          icon: "text-slate-400",
          text: "text-slate-400"
        };
      case "spinning":
        return {
          container: "border-blue-500",
          icon: "text-blue-500",
          text: "text-slate-800"
        };
      case "complete":
        return {
          container: "border-blue-500 bg-white",
          icon: "text-blue-500",
          text: "text-slate-800"
        };
    }
  };

  const s1 = getStepStyles(step1State);
  const s2 = getStepStyles(step2State);

  // Animation Variants for Build Phase
  const containerVariants: Variants = {
    idle: { opacity: 0 },
    building: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.1,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants: Variants = {
    idle: { opacity: 0, y: -10, scale: 0.9 },
    building: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.3, ease: "easeOut" } },
    complete: { opacity: 1, y: 0, scale: 1 }
  };

  // Explicit heights for lines to animate to
  const initialLineHeight = "2.2rem"; // h-20
  const connectingLineHeight = "2rem"; // h-12

  return (
    <motion.div 
      className={cn("flex flex-col items-center", className)}
      initial="idle"
      animate={buildPhase !== "idle" ? "building" : "idle"}
      variants={containerVariants}
    >
      {/* Initial Line from top */}
      <motion.div 
        variants={{
          idle: { height: 0, opacity: 0 },
          building: { height: initialLineHeight, opacity: 1, transition: { duration: 0.3 } }
        }}
        className="w-px bg-slate-200 relative overflow-hidden"
      >
        <motion.div
          initial={{ height: "0%" }}
          animate={initialLineFill ? { height: "100%" } : { height: "0%" }}
          transition={{ duration: 0.4, ease: "linear" }}
          className="absolute top-0 left-0 w-full bg-blue-500"
        />
      </motion.div>

      {/* Step 1: Analyzing Website */}
      <motion.div variants={itemVariants} className="relative z-10">
        <motion.div 
          className={cn(
            "flex items-center gap-3 px-4 py-2 rounded-full bg-white border transition-all duration-300 shadow-sm relative w-60 justify-center",
            s1.container
          )}
          animate={step1State === "spinning" && !animationComplete ? {
            scale: [1, 1.05, 1],
            boxShadow: [
              "0 1px 2px 0 rgb(0 0 0 / 0.05)",
              "0 0 0 4px rgba(59, 130, 246, 0.25)",
              "0 1px 2px 0 rgb(0 0 0 / 0.05)"
            ]
          } : {}}
          transition={{
            duration: 1,
            repeat: step1State === "spinning" && !animationComplete ? Infinity : 0,
            ease: "easeInOut"
          }}
        >
          <Globe className={cn("w-4 h-4 transition-colors duration-300", s1.icon)} />
          <span className={cn("text-sm font-medium transition-colors duration-300", s1.text)}>Analyzing Website</span>
        </motion.div>
      </motion.div>

      {/* Connecting Line 1 */}
      <motion.div 
        variants={{
          idle: { height: 0, opacity: 0 },
          building: { height: connectingLineHeight, opacity: 1, transition: { duration: 0.3 } }
        }}
        className="w-px bg-slate-200 relative overflow-hidden"
      >
        <motion.div
          initial={{ height: "0%" }}
          animate={step1State === "complete" ? { height: "100%" } : { height: "0%" }}
          transition={{ duration: 0.6, ease: "linear" }}
          className="absolute top-0 left-0 w-full bg-blue-500"
        />
      </motion.div>

      {/* Step 2: Categorizing Prompts */}
      <motion.div variants={itemVariants} className="relative z-10">
        <motion.div 
          className={cn(
            "flex items-center gap-3 px-4 py-2 rounded-full bg-white border transition-all duration-300 shadow-sm relative w-60 justify-center",
            s2.container
          )}
          animate={step2State === "spinning" && !animationComplete ? {
            scale: [1, 1.05, 1],
            boxShadow: [
              "0 1px 2px 0 rgb(0 0 0 / 0.05)",
              "0 0 0 4px rgba(59, 130, 246, 0.25)",
              "0 1px 2px 0 rgb(0 0 0 / 0.05)"
            ]
          } : {}}
          transition={{
            duration: 1,
            repeat: step2State === "spinning" && !animationComplete ? Infinity : 0,
            ease: "easeInOut"
          }}
        >
          <ListFilter className={cn("w-4 h-4 transition-colors duration-300", s2.icon)} />
          <span className={cn("text-sm font-medium transition-colors duration-300", s2.text)}>Categorizing Prompts</span>
        </motion.div>
      </motion.div>

      {/* Connecting Line 2 */}
      <motion.div 
        variants={{
          idle: { height: 0, opacity: 0 },
          building: { height: connectingLineHeight, opacity: 1, transition: { duration: 0.3 } }
        }}
        className="w-px bg-slate-200 relative overflow-hidden"
      >
        <motion.div
          initial={{ height: "0%" }}
          animate={step2State === "complete" ? { height: "100%" } : { height: "0%" }}
          transition={{ duration: 0.6, ease: "linear" }}
          className="absolute top-0 left-0 w-full bg-blue-500"
        />
      </motion.div>

      {/* Final Arrow */}
      <motion.div variants={itemVariants} className="relative z-10">
        <div className={cn(
          "flex items-center justify-center w-9 h-9 rounded-full bg-slate-100 border transition-all duration-300",
          arrowState === "active" ? "border-blue-500 text-blue-500" : 
          arrowState === "complete" ? "border-blue-500 text-white bg-blue-500" : 
          "border-slate-200 text-slate-400"
        )}>
          <ArrowDown className="w-5 h-5" />
        </div>
      </motion.div>

      {/* Branching Lines */}
      <div className="relative w-[300px] h-16 -mt-4 -z-0">
        <svg className="w-full h-full" viewBox="0 0 300 64" fill="none" xmlns="http://www.w3.org/2000/svg">
          {/* Main stem from arrow - always visible in gray, animates to blue */}
          <path 
            d="M150 0 V24" 
            stroke="#e2e8f0" 
            strokeWidth="1.5"
          />
          <motion.path 
            d="M150 0 V24" 
            stroke="#3b82f6" 
            strokeWidth="1.5"
            initial={{ pathLength: 0 }}
            animate={linesActive ? { pathLength: 1 } : { pathLength: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          />
          
          {/* Branches - always visible in gray, blue animates in */}
          {[
            "M150 24 C150 44 40 24 40 64",   // Far left
            "M150 24 C150 44 80 24 80 64",   // Mid left
            "M150 24 C150 44 120 24 120 64", // Near left
            "M150 24 C150 44 180 24 180 64", // Near right
            "M150 24 C150 44 220 24 220 64", // Mid right
            "M150 24 C150 44 260 24 260 64", // Far right
          ].map((d, i) => (
            <g key={i}>
              {/* Gray background line - always visible */}
              <path
                d={d}
                stroke="#e2e8f0"
                strokeWidth="1.5"
                fill="none"
              />
              {/* Blue animated line on top */}
              <motion.path
                d={d}
                stroke="#3b82f6"
                strokeWidth="1.5"
                fill="none"
                initial={{ pathLength: 0 }}
                animate={linesActive ? { pathLength: 1 } : { pathLength: 0 }}
                transition={{ duration: 0.4, delay: 0.15, ease: "easeOut" }}
              />
            </g>
          ))}
        </svg>
      </div>
    </motion.div>
  );
}


